import { useEffect, useRef, type RefObject } from 'react';

/**
 * The layers drawn over the app — menus, popovers, overlays, modals, the thread
 * panel — and the two gestures that put one away without pressing its own
 * control: Escape, and a press that lands outside it.
 *
 * Both gestures are questions about what is stacked over what, so every layer
 * registers itself here for as long as it is on screen, and the order it
 * registers in is the stack: a layer that goes up while another is already open
 * opened later, and is therefore drawn over it. Nothing here reads positioning,
 * class names or the DOM tree, which is what keeps the rule from rotting — one
 * layer is over another because it opened later, whether it is `fixed`,
 * `absolute`, portalled to `document.body`, or nested inside a channel message
 * row.
 *
 * Escape goes to the top of the stack and stops there. The arrangement it
 * replaces — every layer listening, and standing down for a key another one
 * has marked consumed — cannot be made to work: the HTML spec runs a microtask
 * checkpoint after each listener returns rather than after the dispatch, so no
 * listener can wait for the others before deciding, and the layer that
 * registered first is also the first to hear the key. Reading the stack settles
 * it without depending on either.
 *
 * A press puts a layer away when it lands outside that layer and outside every
 * layer stacked over it, which is what "outside" has always meant: a press that
 * something drawn over you caught was never a press on you.
 */

/** Where a layer is on screen: the element itself, or a ref that will hold it. */
type LayerBox = Element | null | RefObject<Element | null>;

const boxElement = (box: LayerBox): Element | null =>
  box === null || box instanceof Element ? box : box.current;

export interface DismissibleLayer {
  /** The layer's box, or boxes. A press inside one of them belongs to it. */
  within: LayerBox | LayerBox[];
  /**
   * Escape, while this layer is the topmost one. A layer that leaves it out
   * still takes the key — it is on top, and the key must not fall through to
   * what it covers — but acts on it elsewhere, or not at all: a confirmation
   * whose request is already in flight must not vanish out from under the
   * member.
   */
  onEscape?: () => void;
  /**
   * Narrows which Escapes count, by where the key was pressed. It is what a
   * layer uses to disown a key pressed inside a surface this registry never
   * saw go up — antd hangs every dialog off `document.body` and answers Escape
   * from a stack of its own that nothing here can join. A disowned key is left
   * entirely alone: not acted on, and not marked consumed, because it belongs
   * to whatever put that surface up.
   */
  claimsEscapeOn?: (target: Element) => boolean;
  /** A press outside this layer, and outside everything stacked over it. */
  onOutsidePress?: () => void;
  /**
   * Narrows which of those presses count. A layer that covers part of the app
   * uses it to disown presses on things it is not drawn over.
   */
  dismissesOn?: (target: Element) => boolean;
}

/** A layer and the boxes it is currently drawn in. */
interface StackedLayer {
  layer: DismissibleLayer;
  boxes: Element[];
}

/** Bottom first: the last entry is drawn over all the others. */
const stack: (() => DismissibleLayer)[] = [];

/**
 * The stack as it stands right now, layers with nothing on screen dropped. A
 * layer is on screen while it has a box that is still in the document: one
 * whose boxes are all unmounted, or all torn out from under it, is not drawn
 * anywhere — it cannot be pressed outside of, it covers nothing, and it has no
 * claim on Escape. Boxes are resolved at the moment the gesture arrives, so a
 * ref filled in after registration is read correctly, and a layer that went
 * away without unregistering stops counting on its own.
 */
const onScreen = (): StackedLayer[] => {
  const stacked: StackedLayer[] = [];
  for (const read of stack) {
    const layer = read();
    const within = Array.isArray(layer.within) ? layer.within : [layer.within];
    const boxes = within
      .map(boxElement)
      .filter((box): box is Element => box !== null && box.isConnected);
    if (boxes.length > 0) stacked.push({ layer, boxes });
  }
  return stacked;
};

const handleEscape = (event: KeyboardEvent) => {
  if (event.key !== 'Escape') return;
  const stacked = onScreen();
  const top = stacked[stacked.length - 1];
  if (top === undefined) return;
  const target = event.target;
  const { claimsEscapeOn, onEscape } = top.layer;
  if (claimsEscapeOn !== undefined && (!(target instanceof Element) || !claimsEscapeOn(target))) {
    return;
  }
  // The key is taken whether or not this layer is the one that acts on it, and
  // marking it says so to the listeners outside this registry — ProseMirror's,
  // antd's — that still decide for themselves.
  event.preventDefault();
  onEscape?.();
};

const handlePress = (event: MouseEvent) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const stacked = onScreen();
  const caught = stacked.map(({ boxes }) => boxes.some(box => box.contains(target)));
  // From the top down, so each layer is asked once whatever is over it has
  // already been accounted for.
  let caughtAbove = false;
  for (let i = stacked.length - 1; i >= 0; i--) {
    const { onOutsidePress, dismissesOn } = stacked[i].layer;
    if (!caught[i] && !caughtAbove && onOutsidePress !== undefined) {
      if (dismissesOn === undefined || dismissesOn(target)) onOutsidePress();
    }
    caughtAbove ||= caught[i];
  }
};

/**
 * Escape is claimed in the capture phase, before anything downstream has
 * reacted to it. A layer that closes itself from a handler of its own — the
 * slash-command picker does, out of the composer's key handler — would
 * otherwise already be off the stack by the time a bubbling listener read it,
 * and the key would fall through to whatever it was covering. A press is taken
 * on the way back up instead, where a handler that stops the event still speaks
 * for its own subtree.
 *
 * What that costs is that nothing downstream of the document gets the key at
 * all: ProseMirror drops a keydown whose default is already prevented
 * (`eventBelongsToView` in `prosemirror-view`), so no editor plugin can answer
 * Escape, and a layer an editor plugin builds — the mention suggestion popup —
 * has to answer it from here like every other layer.
 */
const listen = () => {
  document.addEventListener('keydown', handleEscape, true);
  document.addEventListener('mousedown', handlePress);
};

const stopListening = () => {
  document.removeEventListener('keydown', handleEscape, true);
  document.removeEventListener('mousedown', handlePress);
};

/**
 * Puts a layer on the stack until the returned function is called. `read` is
 * consulted afresh for every gesture, so a layer whose box or callbacks change
 * from one render to the next stays where it is on the stack instead of
 * jumping to the top.
 *
 * Unregistering twice, or after the layer is gone, does nothing.
 */
export function registerDismissibleLayer(read: () => DismissibleLayer): () => void {
  // Wrapped so the entry is this registration and no other, even if the same
  // reader is handed in twice.
  const entry = () => read();
  stack.push(entry);
  if (stack.length === 1) listen();

  return () => {
    const at = stack.indexOf(entry);
    if (at === -1) return;
    stack.splice(at, 1);
    if (stack.length === 0) stopListening();
  };
}

export interface DismissibleLayerOptions extends DismissibleLayer {
  /** Nothing is registered while this is false: a closed surface is not a layer. */
  active: boolean;
  /**
   * Holds registration back a frame, for a layer whose own opening gesture is
   * still being dispatched and would otherwise put it away on the way out.
   */
  deferred?: boolean;
}

export function useDismissibleLayer(options: DismissibleLayerOptions): void {
  const latest = useRef(options);
  useEffect(() => {
    latest.current = options;
  });

  const { active, deferred = false } = options;

  useEffect(() => {
    if (!active) return;
    const read = () => latest.current;

    if (!deferred) return registerDismissibleLayer(read);

    let unregister: (() => void) | null = null;
    const frame = requestAnimationFrame(() => {
      unregister = registerDismissibleLayer(read);
    });
    return () => {
      cancelAnimationFrame(frame);
      unregister?.();
    };
  }, [active, deferred]);
}
